export interface DashboardInputs {
  price: number
  productCost: number
  fleteInicial: number
  cancelledPct: number
  deliveredPct: number // % Pedidos Entregados (Efectividad transportadora)
  cpaIdeal: number
}

export interface DashboardMetrics {
  cpaIdeal: number
  cpaCosteado: number
  costoDevoluciones: number
  fleteReal: number
  totalCostos: number
  grossMargin: number
  netProfit: number
  netMargin: number
  cpaBreakEven: number
  roasBreakEven: number
  roasTarget: number
  efectividadReal: number
  // Utility phases
  cpaTarget10: number
  cpaTarget20: number
  // Funnel
  funnelOrders: number
  funnelDispatched: number
  funnelDelivered: number
  dispatchedPct: number
  cancelledPct: number
  deliveredPct: number
  returnedPct: number
  trafficLightState: 'green' | 'yellow' | 'red'
}

export type CountryCode = 'CO' | 'MX' | 'EC' | 'AR'

export interface CountryConfig {
  code: CountryCode
  name: string
  currency: string
  symbol: string
  locale: string
  flag: string
}

export const COUNTRIES: CountryConfig[] = [
  {
    code: 'CO',
    name: 'Colombia',
    currency: 'COP',
    symbol: 'COP $',
    locale: 'es-CO',
    flag: '🇨🇴',
  },
  {
    code: 'MX',
    name: 'México',
    currency: 'MXN',
    symbol: 'MXN $',
    locale: 'es-MX',
    flag: '🇲🇽',
  },
  {
    code: 'EC',
    name: 'Ecuador',
    currency: 'USD',
    symbol: 'USD $',
    locale: 'en-US',
    flag: '🇪🇨',
  },
  {
    code: 'AR',
    name: 'Argentina',
    currency: 'ARS',
    symbol: 'ARS $',
    locale: 'es-AR',
    flag: '🇦🇷',
  },
]

export function getCountry(code: CountryCode): CountryConfig {
  return COUNTRIES.find((c) => c.code === code)!
}

export function calculateMetrics(inputs: DashboardInputs): DashboardMetrics {
  const {
    price,
    productCost,
    fleteInicial,
    cancelledPct,
    deliveredPct,
    cpaIdeal,
  } = inputs

  // FUNNEL
  const dispatchedPct = 100 - cancelledPct
  const returnedPct = 100 - deliveredPct

  // Efectividad Real = (1 - %Cancelados) * %Entregados
  const efectividadReal = ((100 - cancelledPct) / 100) * (deliveredPct / 100)

  // Funnel numbers (base 100)
  const funnelOrders = 100
  const funnelDispatched = Math.round(dispatchedPct)
  const funnelDelivered = Math.round(efectividadReal * 100)

  // Costo Devoluciones = (Flete Inicial * (1 - %Entregados)) / %Entregados
  const costoDevoluciones =
    deliveredPct > 0
      ? (fleteInicial * (1 - deliveredPct / 100)) / (deliveredPct / 100)
      : 0

  // Flete Real = Flete Inicial + Costo Devoluciones
  const fleteReal = fleteInicial + costoDevoluciones

  // CPA Costeado = CPA Meta / Efectividad Real
  const cpaCosteado = efectividadReal > 0 ? cpaIdeal / efectividadReal : 0

  // Total Costos = Producto + Flete Real + CPA Costeado
  const totalCostos = productCost + fleteReal + cpaCosteado

  // Ganancia Neta = Precio - Total Costos
  const netProfit = price - totalCostos

  // Gross Margin (before ads) = Precio - Producto - Flete Real
  const grossMargin = price - productCost - fleteReal

  // Margen Neto = (Ganancia / Precio) * 100
  const netMargin = price > 0 ? (netProfit / price) * 100 : 0

  // CPA Break Even = (Precio - Producto - Flete Real) * Efectividad Real
  const cpaBreakEvenExact = grossMargin * efectividadReal

  // ROAS Break Even = Precio / CPA Break Even
  const roasBreakEvenExact =
    cpaBreakEvenExact > 0 ? price / cpaBreakEvenExact : 0

  // ROAS Objetivo = Precio / CPA Meta
  const roasTargetExact = cpaIdeal > 0 ? price / cpaIdeal : 0

  // Fases de Utilidad
  // 10% Validación: target profit = price * 0.10
  const targetProfit10 = price * 0.1
  const cpaCosteadoMax10 = price - productCost - fleteReal - targetProfit10
  const cpaTarget10 = cpaCosteadoMax10 * efectividadReal

  // 20% Objetivo: target profit = price * 0.20
  const targetProfit20 = price * 0.2
  const cpaCosteadoMax20 = price - productCost - fleteReal - targetProfit20
  const cpaTarget20 = cpaCosteadoMax20 * efectividadReal

  // Traffic Light
  let trafficLightState: 'green' | 'yellow' | 'red'
  if (cpaIdeal < cpaBreakEvenExact * 0.6) {
    trafficLightState = 'green'
  } else if (cpaIdeal < cpaBreakEvenExact) {
    trafficLightState = 'yellow'
  } else {
    trafficLightState = 'red'
  }

  return {
    cpaIdeal,
    cpaCosteado: Math.round(cpaCosteado),
    costoDevoluciones: Math.round(costoDevoluciones),
    fleteReal: Math.round(fleteReal),
    totalCostos: Math.round(totalCostos),
    grossMargin: Math.round(grossMargin),
    netProfit: Math.round(netProfit),
    netMargin: parseFloat(netMargin.toFixed(2)),
    cpaBreakEven: Math.round(cpaBreakEvenExact),
    roasBreakEven: parseFloat(roasBreakEvenExact.toFixed(2)),
    roasTarget: parseFloat(roasTargetExact.toFixed(2)),
    efectividadReal: parseFloat((efectividadReal * 100).toFixed(1)),
    cpaTarget10: Math.round(Math.max(0, cpaTarget10)),
    cpaTarget20: Math.round(Math.max(0, cpaTarget20)),
    funnelOrders,
    funnelDispatched,
    funnelDelivered,
    dispatchedPct: parseFloat(dispatchedPct.toFixed(1)),
    cancelledPct,
    deliveredPct,
    returnedPct,
    trafficLightState,
  }
}

export function formatCurrency(
  value: number,
  countryCode: CountryCode = 'CO',
): string {
  const country = getCountry(countryCode)
  return country.symbol + Math.abs(value).toLocaleString(country.locale)
}

export function formatNumber(
  value: number,
  countryCode: CountryCode = 'CO',
): string {
  const country = getCountry(countryCode)
  return Math.abs(value).toLocaleString(country.locale)
}
